import React from 'react'
import styled from 'styled-components'
import { Section, SectionTitle, SubSection, SubSectionTitle, Text, List, CodeBlock, Table } from '../styles'

export default function SecuritySection() {
  return (
    <Section id="security">
      <SectionTitle>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
        </svg>
        5. Security & Privacy
      </SectionTitle>
      
      <SubSection id="authentication">
        <SubSectionTitle>5.1 Authentication System</SubSectionTitle>
        <Text>
          The platform implements a robust authentication system based on
          blockchain wallet integration:
        </Text>
        <List>
          <li>
            Wallet Authentication:
            <ul>
              <li>Secure, non-custodial wallet integration using industry-standard protocols</li>
            </ul>
          </li>
          <li>
            Transaction Signing:
            <ul>
              <li>All operations requiring authorization are signed using the user's private key</li>
            </ul>
          </li>
          <li>
            Session Management:
            <ul>
              <li>Secure session handling with automatic timeout and renewal mechanisms</li>
            </ul>
          </li>
        </List>
      </SubSection>

      <SubSection id="encryption">
        <SubSectionTitle>5.2 Data Encryption</SubSectionTitle>
        <Text>
          All sensitive data is protected using state-of-the-art encryption:
        </Text>
        <CodeBlock>
{`// Encryption Configuration
{
  "data_at_rest": {
    "algorithm": "AES-256-GCM",
    "key_management": "HSM-backed KMS",
    "rotation_period": "90 days"
  },
  "data_in_transit": {
    "protocol": "TLS 1.3",
    "cipher_suites": [
      "TLS_AES_256_GCM_SHA384",
      "TLS_CHACHA20_POLY1305_SHA256"
    ]
  },
  "key_derivation": {
    "algorithm": "PBKDF2",
    "iterations": 100000,
    "salt_length": 32
  }
}`}
        </CodeBlock>
      </SubSection>

      <SubSection id="access-control">
        <SubSectionTitle>5.3 Access Control</SubSectionTitle>
        <Text>
          The platform implements a sophisticated role-based access control
          system:
        </Text>
        <Table>
          <thead>
            <tr>
              <th>Role</th>
              <th>Permissions</th>
              <th>Scope</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Project Owner</td>
              <td>Full project control</td>
              <td>Project-wide</td>
            </tr>
            <tr>
              <td>Agent Manager</td>
              <td>Agent deployment & monitoring</td>
              <td>Agent-specific</td>
            </tr>
            <tr>
              <td>Auditor</td>
              <td>Read-only access</td>
              <td>Project metrics</td>
            </tr>
          </tbody>
        </Table>
      </SubSection>

      <SubSection id="audit">
        <SubSectionTitle>5.4 Security Auditing</SubSectionTitle>
        <Text>
          Comprehensive security auditing and monitoring ensures system
          integrity:
        </Text>
        <List>
          <li>
            Automated Scanning:
            <ul>
              <li>Continuous vulnerability assessment</li>
              <li>Dependency security checks</li>
              <li>Smart contract auditing</li>
              <li>Code quality analysis</li>
            </ul>
          </li>
          <li>
            Activity Monitoring:
            <ul>
              <li>Real-time threat detection</li>
              <li>Anomaly detection</li>
              <li>Access pattern analysis</li>
              <li>Performance monitoring</li>
            </ul>
          </li>
          <li>
            Compliance:
            <ul>
              <li>SOC 2 Type II compliance</li>
              <li>GDPR compliance</li>
              <li>Regular security assessments</li>
              <li>Incident response planning</li>
            </ul>
          </li>
        </List>
      </SubSection>
    </Section>
  )
}
